// Header.jsx — navigation intégrée (sidebar dans le header)
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification
} from '../../hooks/useNotifications';
import { formatDate } from '../../utils/formatters';
import { getTokenExpiry } from '../../utils/jwt';
import {
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Shield,
  Check,
  Trash2,
  Home,
  FileText,
  MapPin,
  Clipboard,
  Users,
  BarChart3,
  ScrollText,
  Settings as SettingsIcon,
  Search,
  Sparkles,
  Menu,
  X
} from 'lucide-react';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: notificationsData } = useNotifications({}, { page: 1, limit: 20 });
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();
  const deleteNotificationMutation = useDeleteNotification();

  const notifications = notificationsData?.data || [];

  const userMenuRef = useRef(null);
  const notifMenuRef = useRef(null);
  const notifButtonRef = useRef(null);
  const userButtonRef = useRef(null);

  const unreadCount = useMemo(() => notifications.filter(n => !n.lue).length, [notifications]);

  // Durée de session restante (basée sur le refresh token : c'est lui qui
  // détermine jusqu'à quand l'utilisateur peut rester connecté sans se
  // ré-authentifier, le token d'accès étant lui rafraîchi silencieusement
  // toutes les 15 min tant que le refresh token est valide).
  const [sessionExpiry, setSessionExpiry] = useState(null);

  useEffect(() => {
    const computeExpiry = () => {
      const refreshToken = localStorage.getItem('refreshToken');
      setSessionExpiry(getTokenExpiry(refreshToken));
    };
    computeExpiry();
    const interval = setInterval(computeExpiry, 60000);
    return () => clearInterval(interval);
  }, []);

  // Ferme le menu mobile sur changement de route
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const menuItems = useMemo(() => [
    { name: 'Dashboard', path: '/', icon: Home, roles: [], description: 'Vue d\'ensemble' },
    { name: 'Permis', path: '/permis', icon: FileText, roles: [], description: 'Gestion des permis' },
    { name: 'Vérifier', path: '/verify', icon: Search, roles: [], isPublic: true, description: 'Vérification publique' },
    { name: 'Zones', path: '/zones', icon: MapPin, roles: ['HSE'], description: 'Zones de travail' },
    { name: 'Types', path: '/types-permis', icon: Clipboard, roles: ['HSE'], description: 'Types de permis' },
    { name: 'Utilisateurs', path: '/utilisateurs', icon: Users, roles: ['HSE', 'ADMIN'], description: 'Utilisateurs' },
    { name: 'Rapports', path: '/rapports', icon: BarChart3, roles: ['HSE', 'ADMIN', 'SUPERVISEUR'], description: 'Statistiques' },
    { name: 'Audit', path: '/audit-logs', icon: ScrollText, roles: ['HSE', 'ADMIN'], description: 'Journal d\'activité' },
    { name: 'Admin', path: '/admin', icon: SettingsIcon, roles: ['ADMIN'], description: 'Paramètres système', highlight: true }
  ], []);

  const canAccess = useCallback((roles) => {
    if (roles.length === 0) return true;
    return hasRole(...roles);
  }, [hasRole]);

  const isActive = useCallback((path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const visibleMenuItems = useMemo(
    () => menuItems.filter(item => canAccess(item.roles)),
    [menuItems, canAccess]
  );

  const handleClickOutside = useCallback((event) => {
    if (userMenuRef.current && !userMenuRef.current.contains(event.target) &&
      userButtonRef.current && !userButtonRef.current.contains(event.target)) {
      setShowUserMenu(false);
    }
    if (notifMenuRef.current && !notifMenuRef.current.contains(event.target) &&
      notifButtonRef.current && !notifButtonRef.current.contains(event.target)) {
      setShowNotifications(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key?.toLowerCase?.() || '';
      if (key === 'escape') {
        setShowUserMenu(false);
        setShowNotifications(false);
        setMobileMenuOpen(false);
      }
      if (e.altKey && key === 'n') {
        e.preventDefault();
        setShowNotifications(prev => !prev);
        setShowUserMenu(false);
      }
      if (e.altKey && key === 'u') {
        e.preventDefault();
        setShowUserMenu(prev => !prev);
        setShowNotifications(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  }, [logout, navigate]);

  const markAsRead = useCallback((notifId) => {
    markAsReadMutation.mutate(notifId);
  }, [markAsReadMutation]);

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback((notifId) => {
    deleteNotificationMutation.mutate(notifId);
  }, [deleteNotificationMutation]);

  const handleNotificationClick = useCallback((notif) => {
    if (!notif.lue) {
      markAsReadMutation.mutate(notif.id);
    }
    if (notif.permis_id) {
      navigate(`/permis/${notif.permis_id}`);
      setShowNotifications(false);
    }
  }, [markAsReadMutation, navigate]);

  const getRoleLabel = useCallback((role) => {
    const roles = {
      DEMANDEUR: 'Demandeur', SUPERVISEUR: 'Superviseur',
      RESP_ZONE: 'Resp. Zone', HSE: 'HSE', ADMIN: 'Administrateur'
    };
    return roles[role] || role || '';
  }, []);

  const getRoleBadgeColor = useCallback((role) => {
    const colors = {
      DEMANDEUR: 'bg-slate-100 text-slate-700', SUPERVISEUR: 'bg-indigo-100 text-indigo-700',
      RESP_ZONE: 'bg-purple-100 text-purple-700', HSE: 'bg-emerald-100 text-emerald-700',
      ADMIN: 'bg-rose-100 text-rose-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  }, []);

  const getNotifIcon = useCallback((type) => {
    const categories = {
      PERMIS_A_VALIDER: '⏳',
      PERMIS_EN_ATTENTE: '⏳',
      PERMIS_VALIDE: '✅',
      PERMIS_EN_COURS: '✅',
      PERMIS_SUSPENDU: '⚠️',
      PERMIS_REACTIVE: 'ℹ️',
      PERMIS_CLOTURE: 'ℹ️'
    };
    return categories[type] || 'ℹ️';
  }, []);

  const initials = `${user?.prenom?.[0] || ''}${user?.nom?.[0] || ''}` || '?';

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      {/* Barre principale */}
      <div className="flex items-center justify-between h-14 px-4 lg:px-6">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0" aria-label="Accueil - HSE System">
          <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-lg p-1.5 shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all">
            <Shield className="w-4 h-4" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-sm font-bold text-slate-900 leading-tight group-hover:text-indigo-600 transition-colors">HSE System</h1>
            <p className="text-[10px] text-slate-500 leading-tight">Gestion des Permis</p>
          </div>
        </Link>

        {/* Navigation desktop — centre */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 mx-6 overflow-x-auto" aria-label="Navigation principale">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                title={item.description}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-150 ${
                  active
                    ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200'
                    : item.isPublic
                      ? 'text-amber-800 hover:bg-amber-50'
                      : item.highlight
                        ? 'text-purple-800 hover:bg-purple-50'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                <span>{item.name}</span>
                {item.isPublic && !active && (
                  <span className="px-1 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-700 leading-none">PUB</span>
                )}
                {item.highlight && !active && (
                  <Sparkles className="w-3 h-3 text-purple-500" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Actions droite */}
        <div className="flex items-center gap-1">

          {/* Bouton menu mobile */}
          <button
            className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => setMobileMenuOpen(prev => !prev)}
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Notifications */}
          <div className="relative" ref={notifMenuRef}>
            <button
              ref={notifButtonRef}
              onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); }}
              className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label={`Notifications (${unreadCount} non lues)`}
              aria-expanded={showNotifications}
              aria-haspopup="true"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white ring-2 ring-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div
                className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in duration-200 z-50"
                role="menu"
                aria-label="Panneau de notifications"
              >
                <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllAsRead} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 hover:underline">
                      <Check className="w-3 h-3" />Tout marquer lu
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 font-medium">Aucune notification</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`group px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.lue ? 'bg-indigo-50/40' : ''}`}
                          role="menuitem"
                          onClick={() => handleNotificationClick(notif)}
                        >
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 pt-1.5">
                              {!notif.lue && <div className="w-2 h-2 bg-indigo-500 rounded-full ring-2 ring-indigo-100" />}
                            </div>
                            <div className="flex-shrink-0 text-base pt-0.5">{getNotifIcon(notif.type)}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-900 leading-snug font-medium">{notif.message}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{formatDate(notif.date_creation, 'relative')}</p>
                            </div>
                            <div className="flex-shrink-0 flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!notif.lue && (
                                <button onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }} className="p-1 rounded hover:bg-indigo-100 text-indigo-600 transition-colors" title="Marquer comme lu">
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }} className="p-1 rounded hover:bg-rose-100 text-rose-600 transition-colors" title="Supprimer">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Menu utilisateur */}
          <div className="relative" ref={userMenuRef}>
            <button
              ref={userButtonRef}
              onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); }}
              className="flex items-center gap-1.5 px-1.5 lg:px-2 py-1.5 rounded-lg hover:bg-slate-100 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Menu utilisateur"
              aria-expanded={showUserMenu}
              aria-haspopup="true"
            >
              <div className="hidden sm:block text-right">
                <p className="text-xs font-semibold text-slate-900 leading-tight">{user?.prenom} {user?.nom}</p>
                <p className="text-[10px] text-slate-500 leading-tight">{getRoleLabel(user?.role)}</p>
              </div>
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-lg font-bold text-xs shadow-md ring-2 ring-white">
                {initials}
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-500 hidden lg:block transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in duration-200 z-50" role="menu">
                <div className="px-4 py-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-slate-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-11 h-11 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl font-bold text-sm shadow-lg">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{user?.prenom} {user?.nom}</p>
                      <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getRoleBadgeColor(user?.role)}`}>
                    {getRoleLabel(user?.role)}
                  </span>
                  {sessionExpiry && (
                    <p className="text-[11px] text-slate-500 mt-2">
                      Session valide jusqu'au {formatDate(sessionExpiry, 'full')}
                    </p>
                  )}
                </div>
                <div className="py-2">
                  <button onClick={() => { navigate('/profil'); setShowUserMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors" role="menuitem">
                    <User className="w-4 h-4" /><span>Mon profil</span>
                  </button>
                  <button onClick={() => { navigate('/parametres'); setShowUserMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors" role="menuitem">
                    <Settings className="w-4 h-4" /><span>Paramètres</span>
                  </button>
                </div>
                <div className="border-t border-slate-100 py-2">
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors" role="menuitem">
                    <LogOut className="w-4 h-4" /><span>Déconnexion</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Menu mobile déroulant */}
      {mobileMenuOpen && (
        <nav className="lg:hidden border-t border-slate-200 bg-white px-3 py-2 animate-in slide-in-from-top-1 duration-200" aria-label="Navigation mobile">
          <ul className="space-y-0.5" role="list">
            {visibleMenuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : item.isPublic
                          ? 'text-amber-800 hover:bg-amber-50'
                          : item.highlight
                            ? 'text-purple-800 hover:bg-purple-50'
                            : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    <span className="flex-1">{item.name}</span>
                    <span className="text-xs opacity-60">{item.description}</span>
                    {item.isPublic && !active && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700">Public</span>
                    )}
                    {item.highlight && !active && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-700">
                        <Sparkles className="w-2.5 h-2.5" />Admin
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          {/* Info utilisateur mobile */}
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-center w-9 h-9 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-lg font-bold text-xs flex-shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-900 truncate">{user?.prenom} {user?.nom}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}